import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useAdmin } from '../hooks/useAdmin';

export default function DuplicateManager() {
  const { isAdmin } = useAdmin();
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(new Set());

  const detectDuplicates = async () => {
    setLoading(true);
    try {
      const competitionsSnapshot = await getDocs(collection(db, 'competitions'));
      const competitions = [];
      
      competitionsSnapshot.forEach(doc => {
        competitions.push({
          docId: doc.id,
          ...doc.data()
        });
      });
      
      // Group competitions by potential duplicate criteria
      const duplicateGroups = new Map();
      
      competitions.forEach(comp => {
        // Handle different timestamp formats
        let dateOnly = 'no-date';
        if (comp.timestamp) {
          try {
            if (typeof comp.timestamp === 'string') {
              // String format: "2024-12-15T14:30:22"
              dateOnly = comp.timestamp.slice(0, 10);
            } else if (comp.timestamp && typeof comp.timestamp.toDate === 'function') {
              // Firestore Timestamp object
              dateOnly = comp.timestamp.toDate().toISOString().slice(0, 10);
            } else if (comp.timestamp instanceof Date) {
              // JavaScript Date object
              dateOnly = comp.timestamp.toISOString().slice(0, 10);
            } else if (typeof comp.timestamp === 'number') {
              // Unix timestamp
              dateOnly = new Date(comp.timestamp).toISOString().slice(0, 10);
            }
          } catch (error) {
            console.warn('Error parsing timestamp for competition:', comp.docId, comp.timestamp, error);
            dateOnly = 'invalid-date';
          }
        }
        
        // Create a key based on name, discipline, category, dance, and date (ignoring time)
        const key = `${comp.name || 'unnamed'}_${comp.discipline || 'unknown'}_${comp.category || 'unknown'}_${comp.dance || 'unknown'}_${dateOnly}`;
        
        if (!duplicateGroups.has(key)) {
          duplicateGroups.set(key, []);
        }
        duplicateGroups.get(key).push(comp);
      });
      
      // Find groups with multiple competitions (potential duplicates)
      const duplicatesList = [];
      duplicateGroups.forEach((group, key) => {
        if (group.length > 1) {
          // Sort by timestamp, handling different formats
          const sortedGroup = group.sort((a, b) => {
            const getTimestamp = (comp) => {
              if (!comp.timestamp) return 0;
              try {
                if (typeof comp.timestamp === 'string') {
                  return new Date(comp.timestamp).getTime();
                } else if (comp.timestamp && typeof comp.timestamp.toDate === 'function') {
                  return comp.timestamp.toDate().getTime();
                } else if (comp.timestamp instanceof Date) {
                  return comp.timestamp.getTime();
                } else if (typeof comp.timestamp === 'number') {
                  return comp.timestamp;
                }
                return 0;
              } catch (error) {
                console.warn('Error parsing timestamp for sorting:', comp.docId, comp.timestamp);
                return 0;
              }
            };
            
            return getTimestamp(b) - getTimestamp(a); // Newest first
          });
          
          duplicatesList.push({
            key,
            competitions: sortedGroup
          });
        }
      });
      
      setDuplicates(duplicatesList);
    } catch (error) {
      console.error('Error detecting duplicates:', error);
      alert('Error detecting duplicates: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteCompetition = async (docId, competitionName) => {
    if (!window.confirm(`Are you sure you want to delete "${competitionName}"?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setDeleting(prev => new Set(prev).add(docId));
    try {
      await deleteDoc(doc(db, 'competitions', docId));
      
      // Refresh the duplicates list
      await detectDuplicates();
      
      alert('Competition deleted successfully');
    } catch (error) {
      console.error('Error deleting competition:', error);
      alert('Error deleting competition: ' + error.message);
    } finally {
      setDeleting(prev => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    if (isAdmin) {
      detectDuplicates();
    }
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You need admin privileges to access the duplicate manager.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>Duplicate Competition Manager</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={detectDuplicates}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Detecting...' : 'Refresh Duplicates'}
        </button>
      </div>

      {loading && <p>Loading competitions...</p>}
      
      {duplicates.length === 0 && !loading && (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#d4edda', 
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          color: '#155724'
        }}>
          ✅ No duplicate competitions detected
        </div>
      )}

      {duplicates.map((duplicate, index) => (
        <div key={duplicate.key} style={{
          marginBottom: '30px',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '20px',
          backgroundColor: '#f8f9fa'
        }}>
          <h3 style={{ color: '#dc3545', marginBottom: '15px' }}>
            Duplicate Group #{index + 1}
          </h3>
          <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '15px' }}>
            Key: {duplicate.key}
          </p>
          
          <div style={{ display: 'grid', gap: '15px' }}>
            {duplicate.competitions.map((comp, compIndex) => (
              <div key={comp.docId} style={{
                border: '1px solid #ced4da',
                borderRadius: '4px',
                padding: '15px',
                backgroundColor: 'white'
              }}>
                <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>
                      Competition {compIndex + 1}
                    </h4>
                    <p><strong>ID:</strong> {comp.docId}</p>
                    <p><strong>Name:</strong> {comp.name}</p>
                    <p><strong>Timestamp:</strong> {(() => {
                      if (!comp.timestamp) return 'No timestamp';
                      try {
                        if (typeof comp.timestamp === 'string') {
                          return comp.timestamp;
                        } else if (comp.timestamp && typeof comp.timestamp.toDate === 'function') {
                          return comp.timestamp.toDate().toISOString();
                        } else if (comp.timestamp instanceof Date) {
                          return comp.timestamp.toISOString();
                        } else if (typeof comp.timestamp === 'number') {
                          return new Date(comp.timestamp).toISOString();
                        }
                        return String(comp.timestamp);
                      } catch (error) {
                        return `Invalid timestamp: ${comp.timestamp}`;
                      }
                    })()}</p>
                    <p><strong>Discipline:</strong> {comp.discipline}</p>
                    <p><strong>Category:</strong> {comp.category}</p>
                    <p><strong>Dance:</strong> {comp.dance}</p>
                    <p><strong>Participants:</strong> {comp.participants || 'unknown'}</p>
                    <p><strong>Results:</strong> {comp.results?.length || 0}</p>
                  </div>
                  
                  <button
                    onClick={() => deleteCompetition(comp.docId, comp.name)}
                    disabled={deleting.has(comp.docId)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: deleting.has(comp.docId) ? 'not-allowed' : 'pointer',
                      marginLeft: '15px'
                    }}
                  >
                    {deleting.has(comp.docId) ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
